import { RbacPermission } from '~/types/Rbac';

import { checkPermissionsBulk } from './checkPermissionsBulk';

type Pending = {
  permission: RbacPermission;
  resolve: (granted: boolean) => void;
  reject: (error: unknown) => void;
};

let queue: Pending[] = [];
let scheduled = false;

const keyOf = ({ resourceId, resourceType }: RbacPermission) =>
  `${resourceId}:${resourceType}`;

async function flush() {
  const batch = queue;
  queue = [];
  scheduled = false;

  // One request per distinct pair, even when several callers asked for the same
  // one before the flush.
  const distinct = new Map<string, RbacPermission>();
  for (const { permission } of batch) {
    distinct.set(keyOf(permission), permission);
  }

  try {
    const results = await checkPermissionsBulk([...distinct.values()]);
    for (const { permission, resolve } of batch) {
      resolve(
        results[permission.resourceId]?.[permission.resourceType] ?? false,
      );
    }
  } catch (error) {
    for (const { reject } of batch) {
      reject(error);
    }
  }
}

/**
 * Coalesces single-permission checks issued in the same tick into one
 * `/permission/bulk/` request.
 *
 * Pilot answers requests serially, so N separate checks cost N round trips of
 * wall clock. Callers stay unaware of each other — they ask for one pair and
 * await one boolean — while the transport underneath sends a single request per
 * tick. This is what lets each pair be cached independently (so a later call
 * for an already-known pair costs nothing) without paying per-pair round trips
 * on the first load.
 */
export function requestPermission(
  permission: RbacPermission,
): Promise<boolean> {
  const pending = new Promise<boolean>((resolve, reject) => {
    queue.push({ permission, resolve, reject });
  });

  if (!scheduled) {
    scheduled = true;
    // Microtask, so everything mounting in the same commit lands in one batch.
    queueMicrotask(flush);
  }

  return pending;
}
